import React, { useCallback, useRef, useState } from 'react';
import { Loader2, Upload, Play } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { useToast } from '../../hooks/use-toast';
import { getSettings, updateSettings } from '../../services/admin/settings';
import { getHeroVideoConfig, saveHeroVideoConfig, uploadHeroVideoFile, uploadHeroPosterFile } from '../../services/admin/heroVideo';
import { useAdminTheme } from '../../context/AdminThemeContext';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const SettingsPage = () => {
  const { toast } = useToast();
  const { isDark, setTheme } = useAdminTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroVideoLoading, setHeroVideoLoading] = useState(true);
  const [heroVideoSaving, setHeroVideoSaving] = useState(false);
  const [heroVideoUploading, setHeroVideoUploading] = useState(false);
  const heroVideoFileInputRef = useRef(null);
  const heroPosterFileInputRef = useRef(null);
  const skipNextHeroFetchRef = useRef(false);

  const mapHeroVideoForm = useCallback((data) => ({
    id: data.id,
    video_url: data.video_url || data.video_url_mp4 || '',
    poster_url: data.poster_url || data.image_url || '',
    title: data.title ?? '',
    subtitle: data.subtitle ?? '',
    cta_text: data.cta || data.cta_text || 'Alışverişe Başla',
    cta_link: data.link_url || data.link || '/',
    is_active: data.is_active !== false,
  }), []);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const [pendingPosterFile, setPendingPosterFile] = useState(null);
  const [form, setForm] = useState({
    shipping_fee: 0,
    free_shipping_threshold: 0,
    vat_rate: 0,
    announcement: '',
    bank_accounts: '[]',
  });
  const [heroVideoForm, setHeroVideoForm] = useState({
    id: null,
    video_url: '',
    poster_url: '',
    title: '',
    subtitle: '',
    cta_text: 'Alışverişe Başla',
    cta_link: '/',
    is_active: true,
  });

  const fetchSettings = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getSettings();
      setForm({
        shipping_fee: data.shipping_fee ?? 0,
        free_shipping_threshold: data.free_shipping_threshold ?? 0,
        vat_rate: data.vat_rate ?? 0,
        announcement: data.announcement || '',
        bank_accounts: JSON.stringify(data.bank_accounts || [], null, 2),
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (!silent) {
        toast({
          title: 'Hata',
          description: 'Ayarlar yüklenirken bir hata oluştu.',
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useAdminAutoRefresh(fetchSettings, { interval: 0, enabled: !saving });

  const fetchHeroVideo = useCallback(async (silent = false) => {
    if (skipNextHeroFetchRef.current) {
      skipNextHeroFetchRef.current = false;
      return;
    }
    try {
      if (!silent) setHeroVideoLoading(true);
      const data = await getHeroVideoConfig();
      if (data) {
        setHeroVideoForm(mapHeroVideoForm(data));
      }
    } catch (error) {
      console.error('Error fetching hero video:', error);
    } finally {
      if (!silent) setHeroVideoLoading(false);
    }
  }, [mapHeroVideoForm]);

  useAdminAutoRefresh(fetchHeroVideo, {
    interval: 0,
    enabled: !heroVideoSaving && !heroVideoUploading,
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      let bankAccounts = [];
      try {
        bankAccounts = JSON.parse(form.bank_accounts || '[]');
      } catch (error) {
        toast({
          title: 'Hata',
          description: 'Banka hesapları JSON formatında olmalıdır.',
          variant: 'destructive',
        });
        return;
      }
      await updateSettings({
        shipping_fee: Number(form.shipping_fee),
        free_shipping_threshold: Number(form.free_shipping_threshold),
        vat_rate: Number(form.vat_rate),
        announcement: form.announcement,
        bank_accounts: bankAccounts,
      });
      toast({
        title: 'Başarılı',
        description: 'Ayarlar kaydedildi.',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Hata',
        description: 'Ayarlar kaydedilirken bir hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleVideoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const MAX_MB = 100;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({
        title: 'Hata',
        description: `Dosya boyutu ${MAX_MB}MB'dan küçük olmalıdır.`,
        variant: 'destructive',
      });
      return;
    }
    const allowed = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-mp4'];
    const ext = (file.name || '').split('.').pop()?.toLowerCase();
    const validExt = ['mp4', 'webm', 'mov'];
    if (!allowed.includes(file.type) && !validExt.includes(ext)) {
      toast({
        title: 'Hata',
        description: 'Sadece MP4 veya WebM formatı desteklenir. Seçilen: ' + (file.type || file.name || 'bilinmiyor'),
        variant: 'destructive',
      });
      return;
    }
    setPendingVideoFile(file);
    toast({ title: 'Video seçildi', description: '"Hero Videoyu Kaydet" ile yükleyip kaydedin.' });
    e.target.value = '';
  };

  const handlePosterFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const MAX_MB = 5;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({
        title: 'Hata',
        description: `Kapak görseli ${MAX_MB}MB'dan küçük olmalıdır.`,
        variant: 'destructive',
      });
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const ext = (file.name || '').split('.').pop()?.toLowerCase();
    const validExt = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
    if (!allowed.includes(file.type) && !validExt.includes(ext)) {
      toast({
        title: 'Hata',
        description: 'Sadece JPG, PNG veya WebP desteklenir. Seçilen: ' + (file.type || file.name || 'bilinmiyor'),
        variant: 'destructive',
      });
      return;
    }
    setPendingPosterFile(file);
    toast({ title: 'Kapak görseli seçildi', description: '"Hero Videoyu Kaydet" ile yükleyip kaydedin.' });
    e.target.value = '';
  };

  const handleSaveHeroVideo = async () => {
    let videoUrlToSave = heroVideoForm.video_url?.trim() || '';
    let posterUrlToSave = heroVideoForm.poster_url?.trim() || '';
    const hadPendingVideo = !!pendingVideoFile;
    const hadPendingPoster = !!pendingPosterFile;

    if (pendingVideoFile) {
      try {
        setHeroVideoUploading(true);
        const res = await uploadHeroVideoFile(pendingVideoFile);
        videoUrlToSave = res?.video_url || res?.videoUrl || '';
        setPendingVideoFile(null);
        if (!videoUrlToSave) throw new Error('Yükleme başarılı ama URL alınamadı.');
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Video yüklenirken hata oluştu.';
        toast({ title: 'Yükleme başarısız', description: msg, variant: 'destructive' });
        return;
      } finally {
        setHeroVideoUploading(false);
      }
    }
    if (!videoUrlToSave) {
      toast({
        title: 'Hata',
        description: 'Video dosyası seçin veya Video URL girin.',
        variant: 'destructive',
      });
      return;
    }

    if (pendingPosterFile) {
      try {
        setHeroVideoUploading(true);
        const res = await uploadHeroPosterFile(pendingPosterFile);
        posterUrlToSave = res?.poster_url || res?.posterUrl || '';
        setPendingPosterFile(null);
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Kapak görseli yüklenirken hata oluştu.';
        toast({ title: 'Kapak yükleme başarısız', description: msg, variant: 'destructive' });
        return;
      } finally {
        setHeroVideoUploading(false);
      }
    }

    try {
      setHeroVideoSaving(true);
      const dataToSave = { ...heroVideoForm, video_url: videoUrlToSave, poster_url: posterUrlToSave || heroVideoForm.poster_url };
      const saved = await saveHeroVideoConfig(dataToSave);
      const desc = hadPendingVideo || hadPendingPoster
        ? (hadPendingVideo && hadPendingPoster ? 'Video ve kapak yüklendi, kaydedildi!' : hadPendingVideo ? 'Video yüklendi ve kaydedildi!' : 'Kapak görseli yüklendi ve kaydedildi!')
        : 'Hero video kaydedildi!';
      toast({ title: 'Başarılı', description: desc });
      setPendingVideoFile(null);
      setPendingPosterFile(null);
      if (saved?.id) {
        skipNextHeroFetchRef.current = true;
        setHeroVideoForm(mapHeroVideoForm(saved));
      }
      window.dispatchEvent(new CustomEvent('hero-video-saved'));
    } catch (error) {
      console.error('Error saving hero video:', error);
      const detail = error.response?.data?.detail;
      let msg = 'Hero video kaydedilirken bir hata oluştu.';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        msg = first.msg || first.message || JSON.stringify(first);
      } else if (detail?.message) msg = detail.message;
      toast({
        title: 'Kaydetme başarısız',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setHeroVideoSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-gray-600">Ayarlar yükleniyor...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold">Ayarlar</h1>
          <p className="text-muted-foreground mt-1">Ödeme, kargo, üst promo bandı ve anasayfa hero video ayarlarını yönetin.</p>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="general">Genel Ayarlar</TabsTrigger>
            <TabsTrigger value="hero-video">Hero Video</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6 mt-6">
        <div className="rounded-lg border border-border bg-card p-4">
          <Label className="text-base">Panel Görünümü</Label>
          <p className="mt-1 text-sm text-muted-foreground">
            Yönetim panelinde gece veya gündüz modunu seçin.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              type="button"
              variant={isDark ? 'default' : 'outline'}
              onClick={() => setTheme('dark')}
            >
              Gece Modu
            </Button>
            <Button
              type="button"
              variant={!isDark ? 'default' : 'outline'}
              onClick={() => setTheme('light')}
            >
              Gündüz Modu
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="shipping_fee">Kargo Ücreti (₺)</Label>
            <Input
              id="shipping_fee"
              type="number"
              value={form.shipping_fee}
              onChange={(e) => setForm({ ...form, shipping_fee: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="free_shipping_threshold">Ücretsiz Kargo Eşiği (₺)</Label>
            <Input
              id="free_shipping_threshold"
              type="number"
              value={form.free_shipping_threshold}
              onChange={(e) => setForm({ ...form, free_shipping_threshold: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="vat_rate">KDV Oranı (%)</Label>
            <Input
              id="vat_rate"
              type="number"
              value={form.vat_rate}
              onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="announcement">Üst Promo Bandı Metni</Label>
          <Textarea
            id="announcement"
            rows={4}
            value={form.announcement}
            onChange={(e) => setForm({ ...form, announcement: e.target.value })}
            placeholder={'Ücretsiz kargo 500₺ ve üzeri siparişlerde!\nYeni sezon ürünlerinde %20 indirim\nHafta sonu siparişlerinde ekstra avantaj'}
          />
          <p className="text-sm text-gray-500 mt-2">
            Her satır ayrı bir mesaj olarak gösterilir; okuma süresine göre sırayla değişir. Boş
            bırakırsanız şerit gizlenir.
          </p>
        </div>

        <div>
          <Label htmlFor="bank_accounts">Banka Hesapları (JSON)</Label>
          <Textarea
            id="bank_accounts"
            rows={6}
            value={form.bank_accounts}
            onChange={(e) => setForm({ ...form, bank_accounts: e.target.value })}
            placeholder='[{"bank":"Ziraat","iban":"TR00...","name":"GAPPAQ"}]'
          />
          <p className="text-sm text-gray-500 mt-2">
            JSON formatında bankalar: bank, iban, name alanlarını kullanın.
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
          </TabsContent>

          <TabsContent value="hero-video" className="space-y-6 mt-6">
            <div>
              <h2 className="text-xl font-semibold mb-1">Anasayfa Hero Video</h2>
              <p className="text-sm text-gray-600">
                Anasayfanın en üstünde gösterilecek tanıtım videosu. Dosya yükleyin (MP4/WebM, max 100MB) veya URL girin.
              </p>
            </div>
            {heroVideoLoading ? (
              <div className="text-gray-500">Yükleniyor...</div>
            ) : (
              <>
                <input
                  ref={heroVideoFileInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/*,.mp4,.webm,.mov"
                  className="hidden"
                  onChange={handleVideoFileSelect}
                />
                <div className="space-y-2">
                  <Label>Video Dosyası Yükle</Label>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => heroVideoFileInputRef.current?.click()}
                      disabled={heroVideoUploading || heroVideoSaving}
                    >
                      {pendingVideoFile ? (
                        <>✓ {pendingVideoFile.name}</>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Dosya Seç (MP4/WebM)
                        </>
                      )}
                    </Button>
                    {heroVideoForm.video_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(heroVideoForm.video_url, '_blank')}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        Önizle
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    Max 100MB. Dosya seçin, sonra "Hero Videoyu Kaydet" ile yükleyip kaydedin.
                  </p>
                </div>
                <div>
                  <Label htmlFor="hero_video_url">Video URL (opsiyonel – dosya yüklediyseniz otomatik dolar)</Label>
                  <Input
                    id="hero_video_url"
                    placeholder="https://example.com/video.mp4 veya YouTube/Vimeo linki"
                    value={heroVideoForm.video_url}
                    onChange={(e) => setHeroVideoForm({ ...heroVideoForm, video_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Poster / Kapak Görseli</Label>
                  <input
                    ref={heroPosterFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                    className="hidden"
                    onChange={handlePosterFileSelect}
                  />
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => heroPosterFileInputRef.current?.click()}
                      disabled={heroVideoUploading || heroVideoSaving}
                    >
                      {pendingPosterFile ? (
                        <>✓ {pendingPosterFile.name}</>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Dosya Seç (JPG/PNG/WebP)
                        </>
                      )}
                    </Button>
                    <span className="text-sm text-gray-500">veya URL:</span>
                    <Input
                      id="hero_poster_url"
                      placeholder="https://example.com/poster.jpg"
                      value={heroVideoForm.poster_url}
                      onChange={(e) => setHeroVideoForm({ ...heroVideoForm, poster_url: e.target.value })}
                      className="max-w-xs"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Video yüklenmeden önce gösterilecek kapak görseli. Max 5MB.</p>
                </div>
                <div>
                  <Label htmlFor="hero_title">Başlık</Label>
                  <Input
                    id="hero_title"
                    placeholder="GAPPAQ Yeni Sezon"
                    value={heroVideoForm.title}
                    onChange={(e) => setHeroVideoForm({ ...heroVideoForm, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="hero_subtitle">Alt Başlık</Label>
                  <Input
                    id="hero_subtitle"
                    placeholder="Keşfedin"
                    value={heroVideoForm.subtitle}
                    onChange={(e) => setHeroVideoForm({ ...heroVideoForm, subtitle: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hero_cta_text">CTA Buton Metni</Label>
                    <Input
                      id="hero_cta_text"
                      placeholder="Alışverişe Başla"
                      value={heroVideoForm.cta_text}
                      onChange={(e) => setHeroVideoForm({ ...heroVideoForm, cta_text: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="hero_cta_link">CTA Link</Label>
                    <Input
                      id="hero_cta_link"
                      placeholder="/ veya /category/kadin"
                      value={heroVideoForm.cta_link}
                      onChange={(e) => setHeroVideoForm({ ...heroVideoForm, cta_link: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hero_is_active"
                    checked={heroVideoForm.is_active}
                    onCheckedChange={(checked) => setHeroVideoForm({ ...heroVideoForm, is_active: !!checked })}
                  />
                  <Label htmlFor="hero_is_active">Hero videoyu aktif göster</Label>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveHeroVideo}
                    disabled={heroVideoSaving || heroVideoUploading || (!heroVideoForm.video_url?.trim() && !pendingVideoFile)}
                  >
                    {heroVideoSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Kaydediliyor...
                      </>
                    ) : (
                      'Hero Videoyu Kaydet'
                    )}
                  </Button>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
